import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components/macro';

import PageElements from '@mirinae/lessons/components/lesson/PageElements';
import PatternAnimation, { computeAnimationLayout } from '@mirinae/lessons/components/lesson/elements/PatternAnimation';
import { FormattedKoEnPhrase } from '@mirinae/lessons/components/lesson/elements/Phrase';
import { getElementComponent } from '@mirinae/lessons/components/lesson/elementComponentMap';
import { PageTop } from '@mirinae/lessons/components/lesson/pages/DefaultPage';
import { lessonsPalette } from '@mirinae/lessons/modules/defines/styles';

const ExampleButtonsBox = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    width: 100%;
`;

const ExampleButton = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    height: 45px;
    cursor: pointer;
    // padding-top: 13px;
    // padding-right: ${props => (props.last ? 0 : 45)}px;
    padding: 13px 22px 0px 22px;
    border-top: solid 2px ${props => (props.selected ? lessonsPalette.darken[1] : lessonsPalette.grey[6])};
`;

const exampleStyle = {
    koFontWeight: '600', koreanFontSize: '24px',
    enFontWeight: 'normal', englishFontSize: '14px',
};

// custom page renderer for the sentence-form animated examples page (idiom lessons, etc.)
//  and handle to sequencing and layout of the process-animation components (like sentenceFormExample) that
//  are found in the page
const SentenceForm = ({ page }) => {
    const [examples, setExamples] = useState([]);
    const [example, setExample] = useState(null);
    const [sentenceForm, setSentenceForm] = useState(null);
    const width = useRef(0);
    const formRef = useRef(null);

    useEffect(() => {
        // hunt for process components inside the page
        const examples = [];
        page.children.forEach(step => step.children.filter(e => getElementComponent(e) === PatternAnimation).forEach(e => {
            if (e.componentName === 'sentenceForm') {
                setSentenceForm(e);
            } else {
                // pre-compute layout to get widest example & set all animations at that width
                const [, exampleWidth] = computeAnimationLayout(e);
                width.current = Math.max(width.current, exampleWidth);
                examples.push(e);
            }
        }));
        if (examples.length > 0) {
            setExamples(examples);
        }
    }, []);

    const selectExample = (e) => {
        if (formRef.current) {
            formRef.current.style.transition = 'opacity 100ms ease-in';
            formRef.current.style.opacity = 0;
        }
        setExample(e);
    };

    const endExample = () => {
        if (formRef.current) {
            formRef.current.style.transition = 'opacity 350ms linear';
            formRef.current.style.opacity = 1;
        }
        setExample(null);
    };

    const ExamplesButtons = () => (
        <ExampleButtonsBox>
            { !example && examples.map((e, i) => (
                <ExampleButton key={e.index} last={i === examples.length - 1} selected={e === example} element={e} onClick={() => selectExample(e)}>
                    <FormattedKoEnPhrase
                        phrase={(e.fieldsByName.example || e.fieldsByName.Example)}
                        phraseStyle={{ ...exampleStyle, deselected: example && e !== example, noTextTools: true }} />
                </ExampleButton>
            ))}
        </ExampleButtonsBox>
    );

    // render PatternAnimation
    const Animation = () => (example ? (
        <PatternAnimation element={example} width={width.current} autoFirstStep next={endExample} />
    ) : (sentenceForm && (
        <PatternAnimation element={sentenceForm} setRef={formRef} width={width.current} />
    )));

    // select elements that the default PageElements component should layout & render
    const displayFilter = (e) => getElementComponent(e) !== PatternAnimation;

    // provide content components for each of the layout boxes in this page
    const mapComponent = (e) => {
        return e.componentName === 'layoutBox' && (e.name === 'examplesButtonBox' ? ExamplesButtons : Animation);
    };

    // map over page elements for current page-build step
    return (
        <>
            <PageTop page={page} />
            <PageElements page={page} displayFilter={displayFilter} mapComponent={mapComponent} />
        </>
    );
};

export default SentenceForm;
